import React from "react";
import { Box, Typography, Paper, Avatar } from "@mui/material";
import AccessTime from "@mui/icons-material/AccessTime";
import TrendingDown from "@mui/icons-material/TrendingDown";
import moment from "moment";
import { preventabilityStatusStyleArray } from "../../../config";
import { changeTypePillSx, getTypeStyle } from "../../changeRequestTypeStyles";

const getStatusDisplayText = (text) => {
  if (typeof text !== "string") return text;
  return text
    .replace(/[-_]/g, " ")
    .toLowerCase()
    .split(" ")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");
};

const STATUS_DOT = {
  AWAITING_CONTRACTOR_CONFIRMATION: "#fa8c16",
  default: "#0d0d0e",
};

const defaultSeverityStyle = {
  color: "#595959",
  backgroundColor: "#fff",
  border: "1px solid #d9d9d9",
};

function getSeverityBadgeStyle(preventability) {
  const key = (preventability || "").toLowerCase();
  const found = preventabilityStatusStyleArray.find(
    (s) => s.status.toLowerCase() === key
  );
  if (!found) return defaultSeverityStyle;
  return {
    color: found.color,
    backgroundColor: "#fff",
    border: found.border || `1px solid ${found.color}`,
  };
}

const TimelineOverviewCR = ({ data }) => {
  const changeRequest = data;
  const snapshot = React.useMemo(() => {
    try {
      return changeRequest?.snapshot ? JSON.parse(changeRequest.snapshot) : {};
    } catch (e) {
      return {};
    }
  }, [changeRequest?.snapshot]);

  const delayReasons = snapshot?.delayReasons || [];
  const dotColor = STATUS_DOT[changeRequest?.status] ?? STATUS_DOT.default;

  const colSx = {
    minWidth: 0,
    display: "flex",
    flexDirection: "column",
    alignItems: "flex-start",
  };

  return (
    <Box sx={{ mt: 2, width: "100%" }}>
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: { xs: "1fr", sm: "repeat(2, 1fr)", md: "repeat(4, minmax(0, 1fr))" },
          gap: { xs: 2, md: 3 },
          mb: 4,
          width: "100%",
          alignItems: "flex-start",
        }}
      >
        <Box sx={colSx}>
          <Typography sx={{ fontSize: "12px", color: "#949DAA", fontWeight: 500, mb: 0.5 }}>
            Change Type
          </Typography>
          <Typography component="span" sx={changeTypePillSx("TIMELINE")}>
            {getTypeStyle("TIMELINE").text}
          </Typography>
        </Box>
        <Box sx={colSx}>
          <Typography sx={{ fontSize: "12px", color: "#949DAA", fontWeight: 500, mb: 0.5 }}>
            Originator
          </Typography>
          <Typography sx={{ fontSize: "14px", color: "#0d0d0e", fontWeight: 700, textTransform: "capitalize" }}>
            {changeRequest?.source || "—"}
          </Typography>
        </Box>
        <Box sx={colSx}>
          <Typography sx={{ fontSize: "12px", color: "#949DAA", fontWeight: 500, mb: 0.5 }}>
            Status
          </Typography>
          <Box sx={{ display: "flex", alignItems: "center", gap: "8px" }}>
            <Box
              sx={{
                width: "8px",
                height: "8px",
                borderRadius: "50%",
                backgroundColor: dotColor,
                flexShrink: 0,
              }}
            />
            <Typography sx={{ fontSize: "14px", color: "#0d0d0e", fontWeight: 500 }}>
              {getStatusDisplayText(changeRequest?.status) || "—"}
            </Typography>
          </Box>
        </Box>
        <Box sx={colSx}>
          <Typography sx={{ fontSize: "12px", color: "#949DAA", fontWeight: 500, mb: 0.5 }}>
            Created on
          </Typography>
          <Typography sx={{ fontSize: "14px", color: "#0d0d0e", fontWeight: 600 }}>
            {changeRequest?.createdAt
              ? moment(changeRequest.createdAt).format("DD MMM YYYY, h:mm A")
              : "—"}
          </Typography>
        </Box>
      </Box>

      <Typography sx={{ fontSize: "15px", fontWeight: 600, color: "#0d0d0e", mb: 1.5 }}>
        Delay Reasons
      </Typography>

      <Box
        sx={{
          backgroundColor: "#f8f9fa",
          borderRadius: "12px",
          p: { xs: 2, sm: 2.5 },
          display: "flex",
          flexDirection: "column",
          gap: 2,
        }}
      >
        {delayReasons.map((reason, idx) => {
          const severitySx = getSeverityBadgeStyle(reason.preventability);
          const typicalImpact =
            reason.typicalImpact ??
            reason.typical_impact ??
            reason.impact ??
            reason.typicalImpactDescription ??
            "";

          return (
            <Paper
              key={reason.id || idx}
              elevation={0}
              sx={{
                p: { xs: 2, sm: "18px 20px" },
                border: "1px solid #E4E5E7",
                borderRadius: "12px",
                backgroundColor: "#fff",
                boxShadow: "0 1px 2px rgba(15, 23, 42, 0.04)",
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  flexDirection: { xs: "column", sm: "row" },
                  alignItems: { sm: "flex-start" },
                  gap: 2,
                }}
              >
                <Avatar
                  variant="rounded"
                  sx={{
                    width: 34,
                    height: 34,
                    borderRadius: "12px",
                    backgroundColor: "#e6f7ff",
                    flexShrink: 0,
                  }}
                >
                  <AccessTime sx={{ color: "#096dd9", fontSize: 18 }} />
                </Avatar>

                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Box
                    sx={{
                      display: "flex",
                      flexDirection: { xs: "column", sm: "row" },
                      alignItems: { sm: "flex-start" },
                      justifyContent: "space-between",
                      gap: 1,
                      mb: 0.5,
                    }}
                  >
                    <Typography sx={{ fontSize: "15px", fontWeight: 700, color: "#0d0d0e", pr: 1 }}>
                      {reason.delayReason}
                    </Typography>
                    {reason.preventability != null && String(reason.preventability).trim() !== "" && (
                      <Typography
                        component="span"
                        sx={{
                          fontSize: "12px",
                          fontWeight: 600,
                          px: 1.5,
                          py: 0.5,
                          borderRadius: "9999px",
                          flexShrink: 0,
                          ...severitySx,
                        }}
                      >
                        {getStatusDisplayText(String(reason.preventability))}
                      </Typography>
                    )}
                  </Box>

                  <Typography sx={{ fontSize: "12px", color: "#949DAA", mb: 1.25, fontWeight: 500 }}>
                    {reason.category}
                  </Typography>

                  <Typography sx={{ fontSize: "14px", color: "#374151", lineHeight: 1.5, mb: typicalImpact ? 1.5 : 0 }}>
                    {reason.comments}
                  </Typography>

                  {typicalImpact ? (
                    <Box sx={{ mt: 0.5 }}>
                      <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, color: "#cf1322", mb: 0.5 }}>
                        <TrendingDown sx={{ fontSize: 18 }} />
                        <Typography
                          sx={{
                            fontSize: "11px",
                            fontWeight: 700,
                            letterSpacing: "0.06em",
                          }}
                        >
                          TYPICAL IMPACT
                        </Typography>
                      </Box>
                      <Typography sx={{ fontSize: "13px", color: "#4b5563", lineHeight: 1.5, pl: 0.25 }}>
                        {typicalImpact}
                      </Typography>
                    </Box>
                  ) : null}
                </Box>
              </Box>
            </Paper>
          );
        })}
        {delayReasons.length === 0 && (
          <Typography sx={{ fontSize: "14px", color: "#949DAA", py: 2, textAlign: "center" }}>
            No delay reasons found.
          </Typography>
        )}
      </Box>
    </Box>
  );
};

export default TimelineOverviewCR;
